import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Config } from '../../helper';
import { Checkbox, DropdownField, TextAreaField, TextField } from './elements';
import { columnTypeOptions, tableColumnDefaults, tableGroupDefaults } from './default-values';

// Columns of a single group. Each column maps a result field to a table column and picks how the
// cell renders (text / link / list). `attributes` only applies to the Link type.
function GroupColumns({groupPrefix, columns}) {
  return (
    <FieldArray name={`${groupPrefix}.columns`}>
      {({remove, push}) => (
        <div className='table-columns'>
          <details className='mt-3'>
            <summary className='text-white fw-bold fs-6'>Columns ({columns?.length || 0})</summary>
          {columns && columns.length > 0 && columns.map((column, colIdx) => {
            const colPrefix = `${groupPrefix}.columns[${colIdx}]`;
            const isUrl = column.type === Config.ColumnTypes.Url;
            const isLinks = column.type === Config.ColumnTypes.Links;
            const isList = column.type === Config.ColumnTypes.List;
            return (
              <div key={column.id || colIdx} className='row'>
                <div className='col-3'>
                  <TextField name={`${colPrefix}.field`} label='Field' placeholder='e.g. company'/>
                </div>
                <div className='col-3'>
                  <TextField name={`${colPrefix}.label`} label='Label' placeholder='e.g. Company'/>
                </div>
                <div className='col-2'>
                  <DropdownField name={`${colPrefix}.type`} label='Type' options={columnTypeOptions}/>
                </div>
                <div className='col-2 d-flex justify-content-start align-items-end'>
                  {!isList && <Checkbox name={`${colPrefix}.sortable`} label='Sortable'/>}
                </div>
                <div className='col-1 align-self-end my-2'>
                  <button
                    type='button'
                    className='btn btn-danger btn-sm'
                    onClick={() => remove(colIdx)}>×
                  </button>
                </div>
                {(isUrl || isLinks) && (
                  <div className='col-12'>
                    <TextAreaField
                      name={`${colPrefix}.attributes`}
                      label='Link attributes'
                      rows={2}
                      placeholder='{"target": "_blank", "rel": "nofollow noopener"}'/>
                    <small className='form-hint'>
                      Optional JSON of extra link attributes. Defaults to opening in a new tab.
                    </small>
                  </div>
                )}
              </div>
            );
          })}
          <div className='row'>
            <div className='col-12 mt-2'>
              <button
                type='button'
                className='btn btn-outline-primary btn-sm'
                onClick={() => push({...tableColumnDefaults, id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)})}>+ Add column
              </button>
            </div>
          </div>
          </details>
        </div>
      )}
    </FieldArray>
  );
}

// Table view mode sub-form for a single provider. The provider queries every group's index at
// once; this config splits the shared result set into one table per group (by `groupBy`) and
// describes each group's columns. Does not use the flat VIEW_MODE_FIELDS catalog.
export default function ProviderTableViewMode({basePath}) {
  const modePrefix = `${basePath}.viewModeConfigs.${Config.ViewModes.Table}`;
  const { values } = useFormikContext();
  // basePath is openSearchProviders[N] — extract N to navigate values directly.
  const providerIdx = parseInt(basePath.match(/\[(\d+)\]/)?.[1] ?? '0', 10);
  const tableConfig = values.openSearchProviders?.[providerIdx]?.viewModeConfigs?.[Config.ViewModes.Table] || {groups: []};

  return (
    <div className='form-section'>
      <FieldArray name={`${modePrefix}.groups`}>
        {({remove, push}) => {
          const groups = tableConfig.groups || [];

          return (
            <div className='table-groups'>
              <div className='row'>
                <div className='col-12'>
                  <TextField
                    name={`${modePrefix}.groupBy`}
                    label='Group by'
                    placeholder='_index'/>
                  <small className='form-hint'>
                    Result field used to split rows into separate tables (e.g. <code>_index</code>).
                    Leave blank to render a single table.
                  </small>
                </div>
                <div className='col-12'>
                  <DropdownField name={`${modePrefix}.style`} label='Style' options={Config.Table.Styles}/>
                </div>
              </div>

              {groups.map((group, groupIdx) => {
                const groupPrefix = `${modePrefix}.groups[${groupIdx}]`;
                return (
                  <div key={group.id || groupIdx} className='group-settings table-group'>
                    <div className='row'>
                      <div className='col-12 d-flex justify-content-between align-items-center'>
                        <h3 className='h--smallest text-white mt-2'>{group.caption || 'New table'}</h3>
                        <button
                          type='button'
                          className='btn btn-danger btn-sm'
                          onClick={() => remove(groupIdx)}>Remove table
                        </button>
                      </div>
                      <div className='col-4'>
                        <TextField name={`${groupPrefix}.match`} label='Match value' placeholder='e.g. cameras_index'/>
                        <small className='form-hint'>Only rows where the "Group by" field matches this value appear in this table.</small>
                      </div>
                      <div className='col-4'>
                        <TextField name={`${groupPrefix}.caption`} label='Caption' placeholder='e.g. Cameras'/>
                      </div>
                      <div className='col-4'>
                        <TextField name={`${groupPrefix}.htmlId`} label='Anchor ID' placeholder='e.g. cameras'/>
                      </div>
                      <div className='col-12'>
                        <GroupColumns groupPrefix={groupPrefix} columns={group.columns}/>
                      </div>
                      <div className='col-12 mb-2'>
                        <TextAreaField name={`${groupPrefix}.footer`} label='Footer' rows={2}/>
                        <small className='form-hint'>
                          Rendered below the table. Supports standard HTML (links, lists, headings, etc.) — scripts and iframes are stripped.
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className='row'>
                <div className='col-12 mt-3'>
                  <button
                    type='button'
                    className='btn btn-outline-primary btn-sm'
                    onClick={() => push({...tableGroupDefaults, id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2), columns: []})}>+ Add table
                  </button>
                </div>
              </div>
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
}
